import { inject, Injectable } from '@angular/core';
import { CompanyProfile } from '@mkp/company/models';
import {
  AccountMembership,
  AccountMembershipMapper,
  AccountMembershipResource,
} from '@mkp/account-membership/data-access';
import { AccountResource, addLegalEntityToAccountsDto } from '@mkp/account/data-access';
import { CompanyResource, getFilterForLegalEntityIds } from '@mkp/company/data-access';
import { LegalEntityResource } from '@mkp/legal-entity/data-access';
import { forkJoin, map, Observable, OperatorFunction, pipe, switchMap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AccountAggregateService {
  private readonly accountResource = inject(AccountResource);
  private readonly accountMembershipResource = inject(AccountMembershipResource);
  private readonly companyResource = inject(CompanyResource);
  private readonly legalEntityResource = inject(LegalEntityResource);
  private readonly accountMembershipMapper = inject(AccountMembershipMapper);

  refreshAccountAggregate(userId: string) {
    return forkJoin([
      getRefreshAccountsApiCall(this.accountResource, this.legalEntityResource),
      getRefreshAccountMembershipsApiCall(
        userId,
        this.accountMembershipResource,
        this.accountMembershipMapper,
        this.companyResource
      ),
    ]).pipe(
      map(([accounts, { accountMemberships, companyProfiles, totalCount }]) => ({
        accounts,
        accountMembershipsPayload: { accountMemberships, totalCount },
        companyProfiles,
      }))
    );
  }
}

const getRefreshAccountsApiCall = (
  accountResource: AccountResource,
  legalEntityResource: LegalEntityResource
) =>
  accountResource.getAll().pipe(
    map(({ _embedded: { results: accounts } }) => accounts),
    addLegalEntityToAccountsDto(legalEntityResource)
  );

const getRefreshAccountMembershipsApiCall = (
  loggedInUserId: string,
  accountMembershipResource: AccountMembershipResource,
  accountMembershipMapper: AccountMembershipMapper,
  companyResource: CompanyResource
) =>
  accountMembershipResource
    .getWithQuery({ params: { filter: `user.id==${loggedInUserId}`, limit: 200 } })
    .pipe(
      map(({ _embedded: { results: dtos }, totalCount }) => ({ dtos, totalCount })),
      map(({ dtos, totalCount }) => ({
        accountMemberships: dtos.map(accountMembershipMapper.fromJson),
        totalCount,
      })),
      addCompanyProfilesToAccountMemberships(companyResource)
    );

const addCompanyProfilesToAccountMemberships = (
  companyResource: CompanyResource
): OperatorFunction<
  { accountMemberships: AccountMembership[]; totalCount: number },
  { accountMemberships: AccountMembership[]; companyProfiles: CompanyProfile[]; totalCount: number }
> =>
  pipe(
    switchMap(({ accountMemberships, totalCount }) =>
      fetchCompanies(accountMemberships, companyResource).pipe(
        map((companyProfiles) => ({
          accountMemberships,
          companyProfiles,
          totalCount,
        }))
      )
    )
  );

const fetchCompanies = (
  accountMemberships: AccountMembership[],
  companyResource: CompanyResource
): Observable<CompanyProfile[]> => {
  const legalEntityIds = accountMemberships
    .map(({ account }) => account.legalEntityId)
    .filter((id) => id !== undefined);
  return companyResource
    .getCompanies({
      params: getFilterForLegalEntityIds(legalEntityIds),
    })
    .pipe(map((enhancedCompanyEnvelop) => enhancedCompanyEnvelop.companyProfiles));
};
